import { observer } from "mobx-react-lite";
import { ArrowRight, CircleDashed, Settings2 } from "lucide-react";
import { useStore } from "@/hooks/useStore";
import { I_AND_I_TIME_PERIODS } from "@/config/date";
import { DistributionBar, getBreakdownSegments, hasBreakdownInvalidData } from "./DistributionBar";

type IAndIDistributionProps = {
	summary: Dryp.IAndISummary;
	showValues?: boolean;
	onConfigured?: () => void;
};

const PERIOD_LABELS: Record<string, string> = Object.fromEntries(
	I_AND_I_TIME_PERIODS.map(period => [period.value, period.label]),
);

const ConfigureButton = observer(
	({
		structureId,
		onConfigured,
		label = "Configure",
		title,
	}: {
		structureId: Dryp.StructureId;
		onConfigured?: () => void;
		label?: string;
		title?: string;
	}) => {
		const {
			uiStore: { modalStore },
		} = useStore();

		return (
			<button
				type="button"
				title={title}
				onClick={() => modalStore.open("structurePropertiesWizard", { structureId, onClose: onConfigured })}
				className="group inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20"
			>
				<Settings2 className="h-3.5 w-3.5" />
				<span>{label}</span>
				<ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
			</button>
		);
	},
);

const PeriodChip = ({ label }: { label: string }) => (
	<span className="rounded-full border border-border bg-background px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
		{label}
	</span>
);

// Configured, but the selected period has no calculated breakdown. Point the user at the periods that
// do have data, or say it hasn't been calculated yet.
const NoBreakdownForPeriod = ({
	otherPeriods,
	calculatedForThisPeriod,
}: {
	otherPeriods: string[];
	calculatedForThisPeriod: boolean;
}) => {
	if (otherPeriods.length === 0) {
		// A summary row (with a createdAt) means the period was calculated but produced no breakdown;
		// no row means it hasn't been calculated yet.
		return (
			<span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/80">
				<CircleDashed className="h-3.5 w-3.5 shrink-0 opacity-70" />
				{calculatedForThisPeriod ? "No breakdown data" : "Not calculated yet"}
			</span>
		);
	}

	return (
		<div className="flex flex-col gap-1 text-[11px] leading-tight">
			<span className="inline-flex items-center gap-1.5 font-medium text-foreground/70">
				<CircleDashed className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
				No data for this period
			</span>
			<span className="inline-flex flex-wrap items-center gap-1 pl-5 text-muted-foreground/80">
				<span>Select period</span>
				{otherPeriods.map(label => (
					<PeriodChip key={label} label={label} />
				))}
			</span>
		</div>
	);
};

export const IAndIDistribution = observer(({ summary, showValues, onConfigured }: IAndIDistributionProps) => {
	// A breakdown only renders when all three parts have values (the ratios are non-zero). A row that
	// exists but is empty/partial is not a real breakdown, so treat it like "no data for this period".
	if (summary.parts && !hasBreakdownInvalidData(getBreakdownSegments(summary.parts))) {
		return <DistributionBar breakdown={summary.parts} showValues={showValues} />;
	}

	if (summary.structure.isFullyConfiguredForIAndI) {
		const otherPeriods = summary.structure.periodsWithIAndIData
			.filter(period => period !== summary.period.label)
			.map(period => PERIOD_LABELS[period] ?? period);

		return (
			<NoBreakdownForPeriod
				otherPeriods={otherPeriods}
				calculatedForThisPeriod={summary.period.createdAt != null}
			/>
		);
	}

	// Not configured, but a summary was calculated before (createdAt set) - old data exists, the
	// configuration just needs adjusting. Distinguish from a never-configured structure.
	if (summary.period.createdAt != null) {
		return (
			<div className="flex flex-col items-start gap-1">
				<span className="text-[11px] leading-tight text-muted-foreground/80">
					Old data exists, but configuration changes are needed to calculate new I&amp;I data.
				</span>
				<ConfigureButton
					structureId={summary.structure.id}
					onConfigured={onConfigured}
					label="Adjust configuration"
				/>
			</div>
		);
	}

	return <ConfigureButton structureId={summary.structure.id} onConfigured={onConfigured} />;
});
